import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MachineScheduleViewModel,
  ProductionOrderDeviationEntry,
  ProductionOrderDeviationProductionSequence,
  ProductionOrderService,
  DefaultTargetContainerQuantityInfo,
  Quantity,
  ProductionOrderSetupPhaseState,
  SuggestQuantityForLastProducedMaterialViewModel,
  OuterQuantityDetails,
  SearchOrdersViewModel,
  ManualTransportProcessInfo,
  MaterialType,
  ConsumptionForProductionOrderViewModel,
  ProductionOrder
} from 'chronos-core-client';
import { ActiveOrderService, ProductionOrderListService } from '@app/core/global-state';
import { tap } from 'rxjs/operators';
import { MachineScheduleDsService } from '@app/core/data-services/machine-schedule-ds';
import { ShiftFilterType } from '@app/shared/models';
import { ActiveWorkCenterService } from '@app/core/workcenter';
import moment from 'moment';

class ProductionOrderListState
{
  public isInitialized: boolean = false;
  public reset: boolean = true;
  public pageIndex = 1;
  public showFinished: boolean;
  public shiftFilterType?: ShiftFilterType;
  public calculateSecondaryMachineLocationKpi?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProductionOrderDsService {

  private productionOrderListState = new ProductionOrderListState();

  constructor(
    private productionOrderService: ProductionOrderService,
    private machineScheduleDsService: MachineScheduleDsService,
    private productionOrderListService: ProductionOrderListService,
    private activeWorkCenterService: ActiveWorkCenterService,
    private activeOrderService: ActiveOrderService,
  ) {}

  public getProductionOrder(productionOrderId: number): Observable<ProductionOrder> {
    return this.productionOrderService.getProductionOrder({ productionOrderId });
  }

  public setCheckedProductionOrder(productionOrderId: number, wasReopened: boolean, isChecked: boolean): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    if (isChecked) {
      return this.productionOrderService
        .checkProductionOrder({
          productionOrderId,
          body: {
            workCenterId
          }
        })
        .pipe(
          tap(() => {
            this.productionOrderListService.setCheckedProductionOrder(productionOrderId);
          })
        );
    } else {
      return this.productionOrderService
        .uncheckProductionOrder({
          productionOrderId,
          body: {
            workCenterId
          }
        })
        .pipe(
          tap(() => {
            this.productionOrderListService.setUncheckedProductionOrder(productionOrderId, wasReopened);
          })
        );
    }
  }

  public presetupProductionOrder(productionOrderId: number): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.productionOrderService.presetupProductionOrder({
      productionOrderId,
      body: {
        workCenterId
      }
    });
  }

  public getDeviationProductionSequenceReasons(productionOrderId: number): Observable<ProductionOrderDeviationProductionSequence> {
    return this.productionOrderService.getDeviationProductionSequenceReasons({ productionOrderId });
  }

  public setAsNextOrder(productionOrderId: number, body: ProductionOrderDeviationEntry): Observable<void> {
    return this.productionOrderService.setAsNextProductionOrder({
      productionOrderId,
      body
    });
  }

  public loadProductionOrderListByShift(
    showFinished: boolean,
    shiftFilterType?: ShiftFilterType,
    calculateSecondaryMachineLocationKpi?: boolean
  ) : Observable<MachineScheduleViewModel[]> {
    this.productionOrderListState.isInitialized = true;
    this.productionOrderListState.showFinished = showFinished;
    this.productionOrderListState.shiftFilterType = shiftFilterType;
    this.productionOrderListState.calculateSecondaryMachineLocationKpi = calculateSecondaryMachineLocationKpi;

    this.productionOrderListState.pageIndex = 1;
    this.productionOrderListState.reset = true;

    return this.loadNextProductionOrderListByShiftPage();
  }

  public loadNextProductionOrderListByShiftPage(): Observable<MachineScheduleViewModel[]> {
    if (!this.productionOrderListState.isInitialized) {
      throw new Error('Production order list state is not initialized. Call reloadProductionOrderListByShift first.');
    }
    this.productionOrderListService.setLoading(true);
    return this.machineScheduleDsService
      .filterMachineScheduleList(
        this.productionOrderListState.showFinished,
        this.productionOrderListState.pageIndex++,
        this.productionOrderListState.shiftFilterType,
        this.productionOrderListState.calculateSecondaryMachineLocationKpi)
      .pipe(
        tap((productionOrders) => {
          if (this.productionOrderListState.reset) {
            this.productionOrderListService.setProductionOrders(productionOrders);
            this.productionOrderListState.reset = false;
          }
          else {
            this.productionOrderListService.appendProductionOrders(productionOrders);
          }
        })
      );
  }

  public reopenProductionOrder(productionOrderId: number): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.productionOrderService
      .reopenProductionOrder({
        productionOrderId,
        body: {
          workCenterId
        }
      })
      .pipe(
        tap(() => {
          this.productionOrderListService.reopenProductionOrder(productionOrderId);
        })
      );
  }

  public cancelReopenProductionOrder(productionOrderId: number): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.productionOrderService.cancelReopenProductionOrder({
      productionOrderId,
      body: {
        workCenterId
      }
    });
  }

  public resetProductionOrders(): Observable<void> {
    return this.productionOrderService.resetProductionOrders();
  }

  public cancelProductionOrder(productionOrderId: number): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();

    return this.productionOrderService
      .cancelProductionOrder({
        productionOrderId,
        body: {
          workCenterId
        }
      })
      .pipe(
        tap(() => {
          this.removeActiveOrder();
        })
      );
  }

  public removeActiveOrder(): void {
    this.activeOrderService.reset();
  }

  public getDefaultTargetContainerQuantityInfo(productionOrderId: number): Observable<DefaultTargetContainerQuantityInfo> {
    return this.productionOrderService.getDefaultTargetContainerQuantity({ productionOrderId });
  }

  public editProductionOrderSetupPhase(
    setupStartTime: string,
    setupEndTime: string,
    setupQuantity: Quantity,
    setupStartCounter: number,
    setupEndCounter: number,
    productionOrderId: number,
    dialogStartedAt: Date,
    startTimeSelectionLog: string,
    endTimeSelectionLog: string
  ): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    const dialogStartedAtUtc = moment(dialogStartedAt).utc().toISOString();
    return this.productionOrderService.editProductionOrderSetupPhase({
      productionOrderId,
      body: {
        workCenterId,
        productionOrderId,
        setupEndTime,
        setupStartTime,
        setupStartCounter,
        setupEndCounter,
        setupQuantity,
        userInterfaceLogs: `Dialog started at: ${dialogStartedAtUtc}\nStartTimeLog: ${startTimeSelectionLog}\nEndTimeLog: ${endTimeSelectionLog}`
      }
    });
  }

  public getSetupPhaseState(
    productionOrderId: number,
    runId: number,
    calledFromApproveReport: boolean
  ): Observable<ProductionOrderSetupPhaseState> {
    return this.productionOrderService.getSetupPhaseState({
      productionOrderId,
      runId,
      calledFromApproveReport
    });
  }

  public cancelEndProduction(productionOrderId: number): Observable<void> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.productionOrderService.cancelEndProduction({
      productionOrderId,
      body: {
        workCenterId
      }
    });
  }

  public suggestQuantityForLastProducedMaterial(productionOrderId: number): Observable<SuggestQuantityForLastProducedMaterialViewModel> {
    return this.productionOrderService.suggestQuantityForLastProducedMaterial({ productionOrderId });
  }

  public getOuterViewQuantityDetails(productionOrderId: number): Observable<OuterQuantityDetails> {
    return this.productionOrderService.getOuterViewQuantityDetails({ productionOrderId });
  }

  public searchOrders(
    showOpen?: boolean,
    showFinished?: boolean,
    showEnded?: boolean,
    showActive?: boolean,
    externalProductionOrderIdFilter?: string
  ): Observable<SearchOrdersViewModel[]> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.productionOrderService.searchOrders({
      workCenterId,
      ShowOpen: showOpen,
      ShowFinished: showFinished,
      ShowEnded: showEnded,
      ShowActive: showActive,
      ExternalProductionOrderIdFilter: externalProductionOrderIdFilter
    });
  }

  public getManualTransportProcessInfo(productionOrderId: number): Observable<ManualTransportProcessInfo> {
    return this.productionOrderService.getManualTransportProcessInfo({ productionOrderId });
  }

  public getConsumptionForProductionOrder(productionOrderId: number, runId: number): Observable<ConsumptionForProductionOrderViewModel> {
    return this.productionOrderService.getConsumptionForProductionOrder({
      materialType: MaterialType.Primary,
      productionOrderId,
      runId
    });
  }
}
